import React from "react";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  OutlinedInput,
  Typography,
  Slider,
  SliderValueLabelProps,
  Chip,
  SxProps,
  Theme,
} from "@mui/material";

import { PasswordFieldWithVisibilityToggle } from "../common/PasswordFieldWithVisibilityToggle";
import { withSnackbar } from "../../utils/common/SnackbarHoc";
import { enableAiSettingsModelArchitecture } from "../../utils/config";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "../../utils/common/common";

const marks = [
  {
    value: 0,
    label: "0",
  },
  {
    value: 0.1,
    label: "1",
  },
  {
    value: 0.2,
    label: "2",
  },
  {
    value: 0.3,
    label: "3",
  },
  {
    value: 0.4,
    label: "4",
  },
  {
    value: 0.5,
    label: "5",
  },
  {
    value: 0.6,
    label: "6",
  },
  {
    value: 0.7,
    label: "7",
  },
  {
    value: 0.8,
    label: "8",
  },
  {
    value: 0.9,
    label: "9",
  },
  {
    value: 1,
    label: "10",
  },
];

interface IAISettingsProps {
  name: string;
  accordionSettings: any;
  handleAccordionChange: (name: string) => void;
  snackbarShowMessage: any;
  aiSettings: any;
  setAISettings: (values: any) => void;
  kbArticles: any;
  sx?: SxProps<Theme> | undefined;
}

const AISettings = ({
  name,
  accordionSettings,
  handleAccordionChange,
  aiSettings,
  setAISettings,
  kbArticles,
  sx,
}: IAISettingsProps) => {
  const validKBArticles = kbArticles.filter((kb: any) =>
    aiSettings.kbArticles.find((k: any) => k.id === kb.id)
  );

  const handleSelectChange = (e: any) => {
    const { value, name: key } = e.target;
    setAISettings((prevState: any) => {
      const updatedKBArticles = kbArticles.filter((kb: any) =>
        value.includes(kb.id)
      );
      return { ...prevState, [key]: updatedKBArticles };
    });
  };

  const handleInputChange = (e: any) => {
    let value = e.target.value;
    const key = e.target.name;
    if (key == "lengthOfResponse" && value !== "") {
      value = parseInt(value);
    }
    setAISettings((prevState: any) => ({ ...prevState, [key]: value }));
  };

  const sliderValueStyle = (props: SliderValueLabelProps) => {
    const { children, value } = props;

    return (
      <Tooltip
        enterTouchDelay={0}
        placement="top"
        title={value}
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "white",
              color: "#0157FF",
              fontSize: 12,
            },
          },
        }}
      >
        {children}
      </Tooltip>
    );
  };

  return (
    <Box className="accordion-wrap" sx={sx}>
      <Accordion
        expanded={accordionSettings["aiSettings"]}
        onChange={() => handleAccordionChange("aiSettings")}
      >
        <AccordionSummary aria-controls={"panel1-content"} id={"panel1-header"}>
          <Grid container className="acc-prod-icon">
            <Grid item className="acc-prod-icon">
              AI Settings
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {kbArticles.length > 0 && (
              <Grid item xs={12}>
                <FormControl className="custom-select" fullWidth>
                  <InputLabel>
                    Bot will reply based on the status of the selected
                    knowledgebase
                  </InputLabel>
                  <Select
                    labelId="kb-select"
                    id="kb-select"
                    value={validKBArticles.map((kb: any) => kb.id)}
                    label="kbArticles"
                    name="kbArticles"
                    onChange={handleSelectChange}
                    multiple
                    input={
                      <OutlinedInput id="select-multiple-chip" label="Chip" />
                    }
                    renderValue={() => {
                      return (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {validKBArticles.map((kb: any) => {
                            return <Chip key={kb.id} label={kb.name} />;
                          })}
                        </Box>
                      );
                    }}
                  >
                    {kbArticles?.map((value: any) => {
                      return (
                        <MenuItem key={value.id} value={value.id}>
                          {value.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={12}>
              <PasswordFieldWithVisibilityToggle
                placeHolder={"ChatGPT API key"}
                value={aiSettings.openaiKey}
                fieldKey={`${name}-openaiKey`}
                name="openaiKey"
                onChange={handleInputChange}
                isMandatory={true}
              />
            </Grid>
            {enableAiSettingsModelArchitecture && (
              <Grid item xs={12}>
                <FormControl className="custom-select" fullWidth>
                  <InputLabel>
                    Model Architecture
                    <div style={{ display: "inline-block", color: "red" }}>
                      *
                    </div>{" "}
                  </InputLabel>
                  <Select
                    labelId="size-select"
                    id="size-select"
                    value={aiSettings.llmArchitecture}
                    label="size"
                    name="llmArchitecture"
                    onChange={handleInputChange}
                  >
                    <MenuItem value="gpt4">GPT-4</MenuItem>
                    <MenuItem value="gpt3">GPT-3</MenuItem>
                    <MenuItem value="gpthybrid">GPT-Hybrid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl className="custom-select" fullWidth>
                <InputLabel>
                  Bot tone of conversation
                  <div style={{ display: "inline-block", color: "red" }}>
                    *
                  </div>{" "}
                </InputLabel>
                <Select
                  labelId="size-select"
                  id="size-select"
                  value={aiSettings.tone}
                  label="size"
                  name="tone"
                  onChange={handleInputChange}
                >
                  <MenuItem value="polite">Polite</MenuItem>
                  <MenuItem value="humorous">Humorous</MenuItem>
                  <MenuItem value="conversational">Conversational</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item flexGrow={1}>
                  <FormControl className="custom-select" fullWidth>
                    <InputLabel>
                      Approximate length of bots response
                      <div style={{ display: "inline-block", color: "red" }}>
                        *
                      </div>{" "}
                    </InputLabel>

                    <OutlinedInput
                      type="number"
                      inputProps={{ min: 50, max: 500 }}
                      id="lor-select"
                      label="Length of response"
                      name="lengthOfResponse"
                      value={aiSettings.lengthOfResponse}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                </Grid>
                <Grid item>
                  <Typography className="title-sm1">Words</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: 1,
                  borderColor: "#c3c6c8",
                  borderRadius: 2,
                  padding: 2,
                }}
              >
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: "14px" }}>
                    Bot's creativity with responses
                  </Typography>
                </Grid>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <Typography className="title-sm1">Less Creative</Typography>
                  <Typography className="title-sm1">More Creative</Typography>
                </Box>
                <Grid item xs={12}>
                  <Slider
                    disabled={false}
                    marks={marks}
                    step={0.1}
                    max={1}
                    min={0.1}
                    defaultValue={0.5}
                    value={aiSettings.aiCreativity}
                    size="medium"
                    valueLabelDisplay="off"
                    color="primary"
                    name="aiCreativity"
                    onChange={handleInputChange}
                    slots={{
                      valueLabel: sliderValueStyle,
                    }}
                  />
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default withSnackbar(AISettings);
